import { useMemo } from "react";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";
import type { ReportTag } from "@features/report/tag/types/reportTag";
import {
  formatReportTagPeriodSuffix,
  getReportTagStatusLabel,
  isTagOpen,
} from "@core/reports/utils/reportWorkflow";

export type ReportTagScopeParams = {
  churchId?: number;
  districtId?: number;
  nationId?: number;
};

export function useSelectedReportTag({
  reportTagId,
  churchId,
  districtId,
  nationId,
  enabled = true,
}: ReportTagScopeParams & {
  reportTagId: number;
  enabled?: boolean;
}) {
  const hasScope = Boolean(enabled && (churchId || districtId || nationId));
  const { data: tags = [], loading: tagsLoading } = useReportTags({
    churchId: hasScope ? churchId || undefined : undefined,
    districtId: hasScope ? districtId || undefined : undefined,
    nationId: hasScope ? nationId || undefined : undefined,
    all: true,
  });

  const selectedTag = useMemo<ReportTag | undefined>(() => {
    if (!enabled || !hasScope || reportTagId <= 0) return undefined;
    return tags.find((t) => Number(t.report_tag_id) === reportTagId);
  }, [enabled, hasScope, reportTagId, tags]);

  const tagStatusName = selectedTag?.report_tag_status_name ?? null;
  const tagIsOpen =
    tagStatusName == null || tagStatusName === ""
      ? true
      : isTagOpen(tagStatusName);
  const tagStatusLabel = getReportTagStatusLabel(tagStatusName);
  const tagPeriodSuffix = formatReportTagPeriodSuffix(tagStatusName);

  return {
    tags,
    tagsLoading: enabled && hasScope ? tagsLoading : false,
    selectedTag,
    tagStatusName,
    tagStatusLabel,
    tagPeriodSuffix,
    tagIsOpen,
  };
}
